import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import CosmosScreens from "./CosmosScreens";
import { isEmpty } from "@groww-tech-pvt/ella";
import { useAsyncCallback } from "react-async-hook";
import { AOF_STATUS, JOURNEY_TYPES, SEARCH_PARAMS } from "core/constants";
import { hasActiveNriJourney, isNriAccount } from "core/helpers";

import { getSearchParams, useNavigateToRoute } from "core/hooks/useGetParam";

const JourneySelectionScreen = lazy(() => import('./JourneySelectionScreen'));
import { ROUTES } from "src/utils/constants/routes";
import { ValueOf } from "src/utils/types";
import { getIsDomain915 } from "src/utils/config/domainConfig";
import { AppQr } from "..";
import { useHoistValue } from "src/utils/adapter/hoist";
import { getDigilockerParams } from "core/utils/digilockerParamCache";
import { useUserDataStore } from "src/utils/store/UserData";
import { activateJourney } from "src/utils/services";
import OnbLoader from "src/components/OnbLoader";

export const CURRENT_SCREEN = {
  JOURNEY_SELECTION_SCREEN: 'JOURNEY_SELECTION_SCREEN',
  COSMOS_SCREENS: 'COSMOS_SCREENS',
  LOADING_SCREEN: 'LOADING_SCREEN',
  APP_QR_SCREEN: 'APP_QR_SCREEN'
} as const;

const FlowNavigator = () => {
  const { digio_doc_id, journeyType, dataType, status, success } = getSearchParams();
  const is915Domain = getIsDomain915();
  const { userData } = useUserDataStore();
  const { navigateToRoute } = useNavigateToRoute();
  const [currentScreen, setCurrentScreen] = useState<ValueOf<typeof CURRENT_SCREEN>>(CURRENT_SCREEN.LOADING_SCREEN);
  const cachedDigilockerParams = getDigilockerParams();

  const { hoistValue: isMainOnboardingEnabled, isHoistLoading: isMainOnboardingHoistLoading } = useHoistValue({
    hoistKey: 'web-main-onboarding',
    defaultValue: false
  });

  const { hoistValue: isHufOnboardingEnabled, isHoistLoading: isHufOnboardingHoistLoading } = useHoistValue({
    hoistKey: 'web-huf-onboarding',
    defaultValue: false
  });

  const isNriUser = isNriAccount(userData?.account_type);

  const getActiveJourney = useAsyncCallback(activateJourney);
  // Guards against double-firing activateJourney: `loading` updates async, so
  // two calls in the same tick (StrictMode double-invoke) could both slip past it.
  const nriJourneyRequestedRef = useRef(false);

  // "Does user have an active NRI journey right now" — triggers the API call
  // once (ref-guarded) and reports progress until settled.
  const resolveNriActiveJourney = useCallback((): 'loading' | 'no-active-journey' | 'active-journey' => {
    if (getActiveJourney.loading) {
      return 'loading';
    }
    if (!nriJourneyRequestedRef.current && !getActiveJourney.result && !getActiveJourney.error) {
      nriJourneyRequestedRef.current = true;
      getActiveJourney.execute();
      return 'loading';
    }

    // failed call defaults to no active journey, never an implicit pass
    if (getActiveJourney.error || !hasActiveNriJourney(getActiveJourney.result)) {
      return 'no-active-journey';
    }
    return 'active-journey';
  }, [getActiveJourney.loading, getActiveJourney.result, getActiveJourney.error]);

  const switchToAppropriateScreen = useCallback(() => {
    //1. if digilocker params are present, then navigate to cosmos screens
    if (!isEmpty(digio_doc_id) && cachedDigilockerParams) {
      navigateToRoute(ROUTES.ONBOARDING, {
        journeyType: cachedDigilockerParams[SEARCH_PARAMS.JOURNEY_TYPES],
        dataType: cachedDigilockerParams[SEARCH_PARAMS.DATA_TYPE],
        status: status || (success === 'true' ? 'success' : 'failed'),
        digio_doc_id: digio_doc_id,
      });
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //2. if 915 domain, then navigate to cosmos screens directly
    if (is915Domain) {
      const isBaseOnboardingPath = window.location.pathname === ROUTES.ONBOARDING;
      if (isBaseOnboardingPath && isEmpty(journeyType) && isEmpty(digio_doc_id)) {
        navigateToRoute(ROUTES.ONBOARDING, { journeyType: JOURNEY_TYPES.MAIN_ONBOARDING });
        setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
        return;
      }
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //3. /onboarding/nri hit directly: needs NRI account + active NRI_ONBOARDING_V2 journey
    if (window.location.pathname === ROUTES.NRI_ONBOARDING) {
      if (!isNriUser) {
        navigateToRoute(ROUTES.ONBOARDING);
        setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
        return;
      }
      const nriJourneyState = resolveNriActiveJourney();
      if (nriJourneyState === 'loading') {
        setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
        return;
      }
      if (nriJourneyState === 'no-active-journey') {
        navigateToRoute(ROUTES.ONBOARDING);
        setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
        return;
      }
      navigateToRoute(ROUTES.NRI_ONBOARDING, { journeyType: JOURNEY_TYPES.NRI_ONBOARDING_V2, dataType: dataType ?? null });
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //4. NRI/OCI account landing on /onboarding: skip hoist checks, check active journey directly
    if (isEmpty(journeyType) && isNriUser) {
      const nriJourneyState = resolveNriActiveJourney();
      if (nriJourneyState === 'loading') {
        setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
        return;
      }
      if (nriJourneyState === 'active-journey') {
        navigateToRoute(ROUTES.NRI_ONBOARDING, { journeyType: JOURNEY_TYPES.NRI_ONBOARDING_V2, dataType: dataType ?? null });
        setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
        return;
      }
      // no active journey: fall through to hoist-based routing
    }

    //5. wait for hoists before any further routing decision
    if (isMainOnboardingHoistLoading || isHufOnboardingHoistLoading) {
      setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
      return;
    }

    //6. if user is already onboarded, then navigate to cosmos screens
    if (!isEmpty(userData) && userData?.aof_status === AOF_STATUS.COMPLETED && (isEmpty(journeyType) || journeyType === JOURNEY_TYPES.MAIN_ONBOARDING)) {
      setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      return;
    }

    //7. if journey type is present, gate on corresponding hoist flag
    if (!isEmpty(journeyType)) {
      // re-validate on every render: journeyType is a client-controlled URL param
      if (journeyType === JOURNEY_TYPES.NRI_ONBOARDING_V2) {
        if (!isNriUser) {
          navigateToRoute(ROUTES.ONBOARDING);
          setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
          return;
        }
        const nriJourneyState = resolveNriActiveJourney();
        if (nriJourneyState === 'loading') {
          setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
          return;
        }
        if (nriJourneyState === 'no-active-journey') {
          navigateToRoute(ROUTES.ONBOARDING);
          setCurrentScreen(CURRENT_SCREEN.LOADING_SCREEN);
          return;
        }
      }
      if (journeyType === JOURNEY_TYPES.HUF_ONBOARDING || journeyType === JOURNEY_TYPES.MAIN_ONBOARDING) {
        const isJourneyEnabled = journeyType === JOURNEY_TYPES.HUF_ONBOARDING ? isHufOnboardingEnabled : isMainOnboardingEnabled;
        setCurrentScreen(isJourneyEnabled ? CURRENT_SCREEN.COSMOS_SCREENS : CURRENT_SCREEN.APP_QR_SCREEN);
      } else {
        setCurrentScreen(CURRENT_SCREEN.COSMOS_SCREENS);
      }
      return;
    }

    //8. no journeyType: flag-based routing
    if (isHufOnboardingEnabled) {
       // HUF enabled: show selection screen regardless of main onboarding state
      setCurrentScreen(CURRENT_SCREEN.JOURNEY_SELECTION_SCREEN);
    } else if (isMainOnboardingEnabled) {
      // HUF disabled, main enabled: set journeyType, re-routes through step 4
      navigateToRoute(ROUTES.ONBOARDING, { journeyType: JOURNEY_TYPES.MAIN_ONBOARDING });
    } else {
       // Both disabled: show QR screen
      setCurrentScreen(CURRENT_SCREEN.APP_QR_SCREEN);
    }
  }, [
    digio_doc_id,
    cachedDigilockerParams,
    is915Domain,
    journeyType,
    dataType,
    userData,
    status,
    success,
    isNriUser,
    resolveNriActiveJourney,
    getActiveJourney.result,
    getActiveJourney.error,
    isMainOnboardingEnabled,
    isMainOnboardingHoistLoading,
    isHufOnboardingEnabled,
    isHufOnboardingHoistLoading
  ]);

  useEffect(() => {
    switchToAppropriateScreen();
  }, [switchToAppropriateScreen]);

  const getLoadingScreen = () => {
    return <div className="absolute-center height100">
      <OnbLoader />
    </div>;
  };
  switch (currentScreen) {
    case CURRENT_SCREEN.APP_QR_SCREEN:
      return <AppQr />;

    case CURRENT_SCREEN.JOURNEY_SELECTION_SCREEN:
      return (
        <Suspense fallback={getLoadingScreen()}>
          <JourneySelectionScreen
            onShowAppQr={() => setCurrentScreen(CURRENT_SCREEN.APP_QR_SCREEN)}
            activeJourneyAPIOutcome={isNriUser ? { result: getActiveJourney.result, error: getActiveJourney.error } : undefined}
          />
        </Suspense>
      );
    case CURRENT_SCREEN.COSMOS_SCREENS:
      return <CosmosScreens />;
    default:
      return getLoadingScreen();
  }
};


export default FlowNavigator;
